import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as Styles from './styles';

const MINUTES_PER_HOUR = 60;

const toMinutes = (time) => {
    if (!time) return 0;
    const match = String(time).match(/(\d{1,2}):(\d{2})/);
    if (!match) return 0;
    return Number(match[1]) * 60 + Number(match[2]);
};

const formatTime = (minutes) => {
    const normalized = ((minutes % 1440) + 1440) % 1440;
    const hours = Math.floor(normalized / 60);
    const mins = normalized % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const BookingCalendar = ({
    instructor,
    template,
    existingBookings,
    instructorBookings,
    myPersonalBookings,
    learnerId,
    learnerName,
    learners = [],
    onBookingSuccess,
    timeLabels,
    weekDates = [],
    allLogsCache = [],
    onCellClick,
    role = 'instructor',
    workStart = '08:00',
    workEnd = '18:00',
    isMobile = false // 🔑 Safe toggle so web version remains untouched
}) => {
    const [selection, setSelection] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isBooking, setIsBooking] = useState(false);

    const [modalSelectedLearnerId] = useState(
        learnerId || learners[0]?.id || ''
    );

    const [fullLearnerData, setFullLearnerData] = useState(null);
    const [dragState, setDragState] = useState(null);
    const scrollContainerRef = useRef(null);

    // 🔑 Compact row height for mobile (72px), standard desktop height (104px)
    const ROW_HEIGHT = isMobile ? 72 : 104;

    // 🔑 Auto-scroll horizontally straight to today's column on mobile load
    useEffect(() => {
        if (isMobile && scrollContainerRef.current && weekDates.length > 0) {
            const todayStr = formatDateString(new Date());
            const todayIndex = weekDates.findIndex(d => {
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}` === todayStr;
            });
            if (todayIndex > 0) {
                const colWidth = 105;
                scrollContainerRef.current.scrollLeft = (todayIndex - 1) * colWidth;
            }
        }
    }, [isMobile, weekDates]);

    useEffect(() => {
        const targetId = role === 'learner' ? learnerId : modalSelectedLearnerId;

        if (!targetId) {
            setFullLearnerData(null);
            return undefined;
        }

        let cancelled = false;

        fetch(`/api/learners/${targetId}`)
            .then((response) => {
                if (!response.ok) throw new Error('Failed to fetch learner');
                return response.json();
            })
            .then((data) => {
                if (!cancelled && data && !data.error) {
                    setFullLearnerData(data);
                }
            })
            .catch((error) => {
                if (!cancelled) console.error('Error fetching learner profile:', error);
            });

        return () => {
            cancelled = true;
        };
    }, [learnerId, modalSelectedLearnerId, role]);

    const currentLearner =
        fullLearnerData ||
        learners.find(
            (learner) =>
                Number(learner.id) ===
                Number(
                    role === 'learner' ? learnerId : modalSelectedLearnerId
                )
        ) ||
        {};

    const primaryAddress =
        currentLearner.default_pickup_address ||
        currentLearner.address ||
        'Home Address';

    const secondaryAddress = currentLearner.secondary_pickup || null;
    const secondaryLabel = currentLearner.secondary_type
        ? currentLearner.secondary_type.toUpperCase()
        : 'WORK / SCHOOL';

    const activeBookings = useMemo(() => {
        return existingBookings ?? instructorBookings ?? myPersonalBookings ?? [];
    }, [existingBookings, instructorBookings, myPersonalBookings]);

    const getDateOnly = useCallback((value) => {
        if (!value) return null;
        if (value instanceof Date) {
            if (Number.isNaN(value.getTime())) return null;
            return formatDateString(value);
        }
        const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) return `${match[1]}-${match[2]}-${match[3]}`;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return formatDateString(date);
    }, []);

    const getLearnerId = useCallback((item) => {
        if (!item) return null;
        const id = item.learner_id ?? item.student_id ?? item.learnerId ?? item.studentId;
        if (id === undefined || id === null || id === '') return null;
        const numericId = Number(id);
        return Number.isNaN(numericId) ? null : numericId;
    }, []);

    const getStudentColor = useCallback((targetLearnerId) => {
        if (targetLearnerId == null) return isMobile ? '#2563eb' : '#0066cc';
        const palette = isMobile ? [
            '#4f46e5', // Indigo
            '#0d9488', // Teal
            '#7c3aed', // Purple
            '#0284c7', // Sky Blue
            '#db2777', // Rose/Pink
            '#10b981', // Emerald
            '#6366f1'  // Slate Blue
        ] : [
            '#2b6cb0', '#805ad5', '#553c9a', '#dd6b20', '#97266d',
            '#c53030', '#3182ce', '#4c51bf', '#7b341e', '#319795'
        ];
        const index = ((Number(targetLearnerId) % palette.length) + palette.length) % palette.length;
        return palette[index];
    }, [isMobile]);

    const isBookingCompletedForLearner = useCallback(
        (booking, bookingDate) => {
            if (!booking) return false;
            const currentBookingDate = getDateOnly(bookingDate || booking.booking_date || booking.date);
            if (!currentBookingDate) return false;
            const todayStr = getDateOnly(new Date());
            const logsArray = Array.isArray(allLogsCache) ? allLogsCache : allLogsCache?.logs || allLogsCache?.data || [];
            const targetLearnerId = Number(booking.learner_id || booking.learnerId || booking.student_id || booking.studentId || 0);

            const studentLogsForDay = logsArray.filter((log) => {
                if (!log) return false;
                const logDate = getDateOnly(log.lesson_date || log.date || log.booking_date || log.created_at);
                if (logDate !== currentBookingDate) return false;
                const logLearnerId = Number(log.learner_id || log.learnerId || log.student_id || log.studentId || 0);
                return targetLearnerId === 0 || logLearnerId === 0 || targetLearnerId === logLearnerId;
            });

            if (studentLogsForDay.length === 0) return false;
            if (currentBookingDate < todayStr) return true;
            if (currentBookingDate > todayStr) return false;

            const bookingId = booking.id || booking.booking_id;
            const exactMatch = studentLogsForDay.find((log) => {
                const logBookingId = log.booking_id || log.bookingId;
                return bookingId && logBookingId && Number(bookingId) === Number(logBookingId);
            });

            if (!exactMatch) return false;

            const ignoredKeys = [
                'id', 'learner_id', 'learnerId', 'student_id', 'studentId', 'instructor_id', 'instructorId',
                'lesson_date', 'date', 'booking_date', 'created_at', 'updated_at', 'booking_id', 'bookingId',
                'start_time', 'time', 'startTime', 'start'
            ];

            return Object.keys(exactMatch)
                .filter((key) => !ignoredKeys.includes(key))
                .some((key) => Boolean(exactMatch[key]));
        },
        [allLogsCache, getDateOnly]
    );

    const getBookingInterval = useCallback((booking) => {
        const start = toMinutes(booking.booking_time || booking.start_time || booking.time || '00:00');
        const parsedDuration = Number.parseFloat(booking.duration);
        const durationMinutes = Number.isFinite(parsedDuration) && parsedDuration > 0 ? Math.round(parsedDuration * 60) : 60;
        return { start, end: start + durationMinutes };
    }, []);

    const getBlockInterval = useCallback((block) => ({
        start: toMinutes(block.startTime || block.start_time || block.start),
        end: toMinutes(block.endTime || block.end_time || block.end)
    }), []);

    const isPastWeek = useMemo(() => {
        if (!weekDates.length) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return weekDates.every((date) => {
            const day = new Date(date);
            day.setHours(0, 0, 0, 0);
            return day < today;
        });
    }, [weekDates]);

    const filteredTimeLabels = useMemo(() => {
        const startMinutes = toMinutes(workStart);
        const endMinutes = toMinutes(workEnd);
        const firstHour = Math.floor(startMinutes / 60) * 60;
        const lastHour = Math.ceil(endMinutes / 60) * 60;
        const labels = [];

        for (let minutes = firstHour; minutes < lastHour; minutes += MINUTES_PER_HOUR) {
            if (isPastWeek || (minutes >= firstHour && minutes < endMinutes)) {
                labels.push(formatTime(minutes));
            }
        }
        return labels;
    }, [workStart, workEnd, isPastWeek]);

    const getDayTemplates = useCallback(
        (date) => {
            const dateKey = getDateOnly(date);
            return (template || []).filter((item) => {
                const templateDate = item.dateString || getDateOnly(item.date);
                return templateDate === dateKey;
            });
        },
        [template, getDateOnly]
    );

    const getDayBookings = useCallback(
        (date) => {
            const dateKey = getDateOnly(date);
            return activeBookings.filter((booking) => {
                const bookingDate = getDateOnly(booking.booking_date || booking.date);
                return bookingDate === dateKey;
            });
        },
        [activeBookings, getDateOnly]
    );

    const findConflict = useCallback(
        (date, start, end, excludedBookingId = null) => {
            const dayBookings = getDayBookings(date);
            const overlappingBooking = dayBookings.find((booking) => {
                const bookingId = booking.id || booking.booking_id;
                if (
                    excludedBookingId != null &&
                    bookingId != null &&
                    String(bookingId) === String(excludedBookingId)
                ) {
                    return false;
                }
                const interval = getBookingInterval(booking);
                return start < interval.end && end > interval.start;
            });

            if (overlappingBooking) {
                return { type: 'booking', item: overlappingBooking };
            }

            const overlappingBlock = getDayTemplates(date).find((block) => {
                if (block.type !== 'blocked') return false;
                const interval = getBlockInterval(block);
                return (
                    interval.end > interval.start &&
                    Math.max(start, interval.start) < Math.min(end, interval.end)
                );
            });

            if (overlappingBlock) {
                return { type: 'blocked', item: overlappingBlock };
            }

            return null;
        },
        [getDayBookings, getDayTemplates, getBookingInterval, getBlockInterval]
    );

    const openNewBooking = useCallback(
        (date, startTime) => {
            const start = toMinutes(startTime);
            const end = start + 60;
            if (findConflict(date, start, end)) {
                alert('This time overlaps an existing lesson or blocked period.');
                return;
            }
            const dateString = getDateOnly(date);
            setSelection({
                date,
                dateString,
                startTime: formatTime(start),
                endTime: formatTime(end),
                pickupLocation: primaryAddress
            });
            setShowConfirmModal(true);
        },
        [findConflict, getDateOnly, primaryAddress]
    );

    const handleCellClick = useCallback(
        (date, time) => {
            const start = toMinutes(time);
            const slotEnd = start + 60;
            
            const dayBlocks = getDayTemplates(date);
            const overlappingBlock = dayBlocks.find(block => {
                if (block.type !== 'blocked') return false;
                const interval = getBlockInterval(block);
                return Math.max(start, interval.start) < Math.min(slotEnd, interval.end);
            });

            if (overlappingBlock) {
                const interval = getBlockInterval(overlappingBlock);
                if (interval.start === start && interval.end < slotEnd) {
                    const adjustedStart = interval.end;
                    const adjustedEnd = adjustedStart + 60;
                    
                    const conflict = findConflict(date, adjustedStart, adjustedEnd);
                    if (!conflict) {
                        if (onCellClick) {
                            onCellClick(date, formatTime(adjustedStart), false, null, false, false, false);
                        }
                        return;
                    }
                }

                if (onCellClick) {
                    onCellClick(
                        date,
                        time,
                        false,
                        null,
                        new Date(date).setHours(Math.floor(start / 60), start % 60, 0, 0) < Date.now(),
                        false,
                        true
                    );
                }
                return;
            }

            const end = start + 60;
            const conflict = findConflict(date, start, end);

            if (conflict) {
                const booking = conflict.item;
                const bookingInterval = getBookingInterval(booking);
                const isPast = new Date(date).setHours(Math.floor(bookingInterval.start / 60), bookingInterval.start % 60, 0, 0) < Date.now();
                const completed = isBookingCompletedForLearner(booking, booking.booking_date || date);
                const isMyBooking = role === 'learner' && Number(getLearnerId(booking)) === Number(learnerId);

                if (role === 'learner' && !isMyBooking) return;
                if (role === 'learner' && isPast) {
                    alert('This past lesson can no longer be modified.');
                    return;
                }

                if (onCellClick) {
                    onCellClick(date, time, true, booking, isPast, completed, false);
                }
                return;
            }

            const cellDateTime = new Date(date);
            cellDateTime.setHours(Math.floor(start / 60), start % 60, 0, 0);

            if (cellDateTime < new Date()) return;

            if (onCellClick) {
                onCellClick(date, time, false, null, false, false, false);
            } else if (role === 'learner') {
                openNewBooking(date, time);
            }
        },
        [findConflict, getDayTemplates, getBlockInterval, getBookingInterval, isBookingCompletedForLearner, getLearnerId, learnerId, role, onCellClick, openNewBooking]
    );

    const beginDrag = (event, date, time) => {
        if (event.button !== 0) return;
        const start = toMinutes(time);
        const end = start + 60;

        if (findConflict(date, start, end)) return;

        const cellDateTime = new Date(date);
        cellDateTime.setHours(Math.floor(start / 60), start % 60, 0, 0);
        if (cellDateTime < new Date()) return;

        setDragState({ date, startTime: start, currentTime: start });
    };

    const updateDrag = (event, date, time) => {
        if (!dragState) return;
        if (getDateOnly(date) !== getDateOnly(dragState.date)) return;
        setDragState((previous) => ({ ...previous, currentTime: toMinutes(time) }));
    };

    const finishDrag = useCallback(() => {
        if (!dragState) return;
        const start = Math.min(dragState.startTime, dragState.currentTime);
        const end = start + 60;
        setDragState(null);

        if (findConflict(dragState.date, start, end)) {
            alert('This time overlaps an existing lesson or blocked period.');
            return;
        }

        if (role !== 'learner') return;

        const dateString = getDateOnly(dragState.date);
        setSelection({
            date: dragState.date,
            dateString,
            startTime: formatTime(start),
            endTime: formatTime(end),
            pickupLocation: primaryAddress
        });
        setShowConfirmModal(true);
    }, [dragState, findConflict, role, getDateOnly, primaryAddress]);

    useEffect(() => {
        if (!dragState) return undefined;
        const handleWindowMouseUp = () => finishDrag();
        window.addEventListener('mouseup', handleWindowMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleWindowMouseUp);
        };
    }, [dragState, finishDrag]);

    const processBooking = async () => {
        if (!selection || isBooking) return;

        const start = toMinutes(selection.startTime);
        const end = toMinutes(selection.endTime);
        const duration = (end - start) / 60;

        if (duration <= 0) {
            alert('Please select a valid lesson duration.');
            return;
        }

        const cleanDateStr = getDateOnly(selection.date) || selection.dateString;
        const conflict = findConflict(cleanDateStr, start, end);

        if (conflict) {
            if (conflict.type === 'blocked') {
                alert('❌ Cannot schedule or move a lesson over a blocked time period.');
            } else {
                alert('❌ This time overlaps an existing lesson. Please choose another time.');
            }
            return;
        }

        setIsBooking(true);

        const targetLearnerId = role === 'instructor' ? modalSelectedLearnerId : learnerId;
        const targetLearner = learners.find((learner) => Number(learner.id) === Number(targetLearnerId));
        const finalLearnerName = role === 'instructor' ? targetLearner?.name || 'Student' : learnerName;

        try {
            const response = await fetch('/api/bookings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    instructor_id: instructor?.id || instructor,
                    learner_id: targetLearnerId,
                    learner_name: finalLearnerName,
                    booking_date: selection.dateString,
                    booking_time: selection.startTime,
                    duration,
                    pickup_location: selection.pickupLocation || primaryAddress
                })
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || 'Booking failed');

            alert('🎉 Booking Secured!');
            setShowConfirmModal(false);
            setSelection(null);
            if (onBookingSuccess) onBookingSuccess();
        } catch (error) {
            alert('❌ ' + error.message);
        } finally {
            setIsBooking(false);
        }
    };

    const changeDuration = (hours) => {
        if (!selection) return;
        const start = toMinutes(selection.startTime);
        const end = start + hours * 60;

        const cleanDateStr = getDateOnly(selection.date) || selection.dateString;
        const bookingId = selection.booking?.id || selection.booking?.booking_id || null;
        const conflict = findConflict(cleanDateStr, start, end, bookingId);

        if (conflict) {
            if (conflict.type === 'blocked') {
                alert('❌ Cannot extend lesson duration into a blocked time period.');
            } else {
                alert('❌ That duration overlaps an existing lesson or blocked period.');
            }
            return;
        }

        setSelection((previous) => ({ ...previous, endTime: formatTime(end) }));
    };

    const changePickupLocation = (pickupLocation) => {
        setSelection((previous) => ({ ...previous, pickupLocation }));
    };

    const getBlockStyle = (date, interval) => {
        const visibleStart = toMinutes(workStart);
        const visibleEnd = toMinutes(workEnd);
        const clippedStart = Math.max(interval.start, visibleStart);
        const clippedEnd = Math.min(interval.end, visibleEnd);

        if (clippedEnd <= clippedStart) return null;

        const offsetMinutes = clippedStart - visibleStart;
        const durationMinutes = clippedEnd - clippedStart;

        return {
            position: 'absolute',
            top: (offsetMinutes / 60) * ROW_HEIGHT,
            height: Math.max(18, (durationMinutes / 60) * ROW_HEIGHT - 2)
        };
    };

const renderBookingBlock = (booking, interval, key, date) => {
        const blockStyle = getBlockStyle(date, interval);
        if (!blockStyle) return null;

        const start = interval.start;
        const end = interval.end;
        const visibleStart = toMinutes(workStart);
        const offset = start - visibleStart;

        const isCompleted = isBookingCompletedForLearner(booking, booking.booking_date || date);
        const startDateTime = new Date(date);
        startDateTime.setHours(Math.floor(start / 60), start % 60, 0, 0);
        const inPast = startDateTime < new Date();
        const isMine = role === 'learner' && Number(getLearnerId(booking)) === Number(learnerId);
        const blockedForLearner = role === 'learner' && !isMine;

        // 🔑 Vibrant student color base
        const studentColor = role === 'instructor' ? getStudentColor(getLearnerId(booking)) : '#2563eb';

        // 🔑 Slick Diagonal Hatch / Striped Background for Past & Completed Lessons
        let background = studentColor;
        let border = isMobile ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.75)';
        let opacity = 1;

        if (isCompleted) {
            // Completed: Vibrant emerald/teal base with subtle diagonal stripe overlay
            background = isMobile 
                ? `repeating-linear-gradient(45deg, #059669, #059669 8px, #047857 8px, #047857 16px)` 
                : '#22c55e';
            border = '1px solid #34d399';
        } else if (inPast) {
            // Past & Unlogged: Vibrant amber/orange with diagonal warning hatch
            background = isMobile 
                ? `repeating-linear-gradient(45deg, #d97706, #d97706 8px, #b45309 8px, #b45309 16px)` 
                : '#d97706';
            border = '1px solid #fbbf24';
        }

return (
            <button
                key={key}
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    if (blockedForLearner) return;
                    if (role === 'learner' && inPast) {
                        alert('This past lesson is locked and can no longer be modified.');
                        return;
                    }
                    if (onCellClick) {
                        onCellClick(date, formatTime(start), true, booking, inPast, isCompleted, false);
                    }
                }}
                style={{
                    position: 'absolute',
                    zIndex: 8,
                    top: (offset / 60) * ROW_HEIGHT,
                    left: '3px',
                    right: '3px',
                    height: Math.max(20, ((end - start) / 60) * ROW_HEIGHT - 3),
                    boxSizing: 'border-box',
                    padding: isMobile ? '4px 6px' : '5px',
                    background,
                    color: '#ffffff',
                    border,
                    borderRadius: isMobile ? '8px' : '6px',
                    fontSize: isMobile ? '10px' : '11px',
                    fontWeight: 700,
                    textAlign: 'left',
                    cursor: blockedForLearner ? 'not-allowed' : 'pointer',
                    overflow: 'hidden',
                    overflowWrap: 'anywhere',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    boxShadow: isMobile ? '0 2px 5px rgba(0,0,0,0.3)' : undefined
                }}
            >
                <span style={isMobile ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' } : {}}>
                    {isCompleted ? '🔒 ' : inPast ? '⚠️ ' : '🎓 '}
                    {role === 'learner' 
                        ? (isCompleted ? 'Completed' : (isMine ? 'My Lesson' : 'Booked Slot')) 
                        : booking.learner_name || 'Booked Lesson'}
                </span>
                
                <span style={{ fontSize: isMobile ? '9px' : '10px', opacity: 0.9, marginTop: '2px' }}>
                    {formatTime(start)} – {formatTime(end)}
                </span>

                {/* 🔑 Updated: Render address on mobile & desktop whenever location exists */}
                {booking.pickup_location && (
                    <span style={{ fontSize: '9px', opacity: 0.8, marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                        📍 {booking.pickup_location}
                    </span>
                )}
            </button>
        );
    };

const renderBlockedBlock = (block, interval, key, date, cellIsPast) => {
        const blockStyle = getBlockStyle(date, interval);
        if (!blockStyle) return null;

        const start = interval.start;
        const end = interval.end;
        const visibleStart = toMinutes(workStart);

        return (
            <button
                key={key}
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    if (onCellClick) {
                        onCellClick(date, formatTime(start), false, null, cellIsPast, false, true);
                    }
                }}
                style={{
                    position: 'absolute',
                    zIndex: 9,
                    top: ((start - visibleStart) / 60) * ROW_HEIGHT,
                    left: '3px',
                    right: '3px',
                    height: Math.max(20, ((end - start) / 60) * ROW_HEIGHT - 3),
                    boxSizing: 'border-box',
                    padding: '4px 6px',
                    // 🔑 Slick dark crimson red diagonal hatch pattern
                    background: isMobile 
                        ? `repeating-linear-gradient(45deg, #991b1b, #991b1b 6px, #7f1d1d 6px, #7f1d1d 12px)` 
                        : '#ef4444',
                    color: '#ffffff',
                    border: isMobile ? '1px solid #f87171' : '1px solid #dc2626',
                    borderRadius: '8px',
                    fontSize: isMobile ? '10px' : '11px',
                    fontWeight: 700,
                    textAlign: 'left',
                    cursor: isMobile ? 'pointer' : 'not-allowed',
                    overflow: 'hidden',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                }}
            >
                🚫 Blocked ({formatTime(start)}–{formatTime(end)})
            </button>
        );
    };

    // 🔑 Conditional variables for slick dark mobile vs standard web
// Dark calendar theme
const calendarBg = isMobile ? '#0f172a' : '#ffffff';
const borderColor = isMobile ? '#19263A' : '#cbd5e0';
const headerBg = isMobile ? '#17243A' : '#f8f9fa';
const timeColBg = isMobile ? '#131F32' : '#ffffff';
const textColor = isMobile ? '#DCE6F4' : '#1a202c';
const subTextColor = isMobile ? '#8295B1' : '#4a5568';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
            <div 
                ref={scrollContainerRef}
                style={{ 
                    ...Styles.cardStyle, 
                    background: calendarBg, 
                    overflowX: 'auto', 
                    overflowY: 'hidden',
                    borderRadius: isMobile ? '16px' : '8px',
                    border: isMobile ? '1px solid rgba(255,255,255,0.08)' : undefined,
                    padding: 0
                }}
            >
                <div
                    style={{
                        ...Styles.gridWrapper,
                        display: 'grid',
                        gridTemplateColumns: isMobile ? `70px repeat(${weekDates.length}, minmax(105px, 1fr))` : `80px repeat(${weekDates.length}, minmax(100px, 1fr))`,
                        gridTemplateRows: `auto repeat(${filteredTimeLabels.length}, ${ROW_HEIGHT}px)`,
                        position: 'relative',
                        alignItems: 'stretch',
                        minWidth: isMobile ? `${70 + weekDates.length * 105}px` : '100%'
                    }}
                >
                    {/* 🔑 Sticky Time Header on Mobile */}
                    <div style={{ 
                        ...(isMobile ? {
                            background: headerBg,
                            color: subTextColor,
                            fontWeight: 800,
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderBottom: `1px solid ${borderColor}`,
                            borderRight: `1px solid ${borderColor}`,
                            position: 'sticky',
                            left: 0,
                            zIndex: 20
                        } : Styles.timeLabelHeader),
                        gridColumn: 1, 
                        gridRow: 1 
                    }}>
                        Time
                    </div>

                    {weekDates.map((date, index) => {
                        const isToday = isMobile && (getDateOnly(date) === getDateOnly(new Date()));
                        return (
                            <div
                                key={getDateOnly(date) || index}
                                style={{ 
                                    ...(isMobile ? {
                                        background: '#17243A',
                                        color: textColor,
                                        padding: '8px 4px',
                                        textAlign: 'center',
                                        borderBottom: `1px solid ${borderColor}`,
                                        borderRight: `1px solid ${borderColor}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '3px'
                                    } : Styles.dayHeader),
                                    gridColumn: index + 2, 
                                    gridRow: 1 
                                }}
                            >
                                <span style={{ fontSize: '10px', fontWeight: 700, color: isToday ? '#93C5FD' : '#8295B1', letterSpacing: '0.5px' }}>
                                    {date.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase()}
                                </span>
                                
                                <div style={{
                                    width: isMobile ? '28px' : 'auto',
                                    height: isMobile ? '28px' : 'auto',
                                    borderRadius: '50%',
background: isToday ? '#3478D4' : 'transparent',
color: isToday ? '#FFFFFF' : '#DCE6F4',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '13px',
                                    fontWeight: 800,
                                    boxShadow: isToday ? '0 2px 8px rgba(59,130,246,0.4)' : 'none'
                                }}>
                                    {date.getDate()}
                                </div>
                            </div>
                        );
                    })}

                    {filteredTimeLabels.map((time, hourIndex) => {
                        const hourStart = toMinutes(time);
                        const row = hourIndex + 2;
                        const isFirstRow = hourIndex === 0;

                        return (
                            <React.Fragment key={time}>
                                {/* 🔑 Sticky Time Column Cells on Mobile */}
                                <div
                                    style={{
                                        ...(isMobile ? {
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            color: subTextColor,
                                            background: timeColBg,
                                            borderBottom: `1px solid ${borderColor}`,
                                            borderRight: `1px solid ${borderColor}`,
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 15
                                        } : Styles.timeLabelCell),
                                        gridColumn: 1,
                                        gridRow: row,
                                        height: ROW_HEIGHT,
                                        boxSizing: 'border-box',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'center',
                                        paddingTop: isMobile ? '8px' : '5px'
                                    }}
                                >
                                    {time}
                                </div>

                                {weekDates.map((date, dayIndex) => {
                                    const dateKey = getDateOnly(date);
                                    const dayTemplates = getDayTemplates(date);
                                    const dayBookings = getDayBookings(date);

                                    const cellDateTime = new Date(date);
                                    cellDateTime.setHours(Math.floor(hourStart / 60), hourStart % 60, 0, 0);
                                    const cellIsPast = cellDateTime < new Date();

                                    const activeBlock = dayTemplates.find((item) => {
                                        if (item.type !== 'blocked') return false;
                                        const interval = getBlockInterval(item);
                                        return Math.max(hourStart, interval.start) < Math.min(hourStart + 60, interval.end);
                                    });

                                    const isOthersBooking = dayBookings.find((booking) => {
                                        const interval = getBookingInterval(booking);
                                        return hourStart < interval.end && hourStart + 60 > interval.start;
                                    });

                                    const othersBlockCheck = isOthersBooking && role === 'learner' && Number(getLearnerId(isOthersBooking)) !== Number(learnerId);
                                    
        const baseBg = isMobile
    ? '#111B2D'
    : (activeBlock || othersBlockCheck ? '#fee2e2' : '#ffffff');

                                    const handleSlotClick = () => {
                                        const exactTime = formatTime(hourStart);
                                        handleCellClick(date, exactTime);
                                    };

                                    return (
                                        <div
                                            key={`${dateKey}-${time}`}
                                            style={{
                                                position: 'relative',
                                                gridColumn: dayIndex + 2,
                                                gridRow: row,
                                                width: '100%',
                                                height: ROW_HEIGHT,
                                                minWidth: 0,
                                                boxSizing: 'border-box',
                                      borderBottom: isMobile ? `1px solid ${borderColor}` : '1px solid #cbd5e0',
borderRight: isMobile ? `1px solid ${borderColor}` : '1px solid #cbd5e0',
                                                background: baseBg,
                                                overflow: 'visible'
                                            }}
                                        >
                                            <button
                                                type="button"
                                                aria-label={`${dateKey} ${time}`}
                                                onClick={handleSlotClick}
                                                onMouseDown={(event) => beginDrag(event, date, time)}
                                                onMouseEnter={(event) => updateDrag(event, date, time)}
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    padding: 0,
                                                    margin: 0,
                                                    border: 'none',
                                                    background: 'transparent',
                                                    cursor: cellIsPast ? 'not-allowed' : 'pointer',
                                                    zIndex: 1
                                                }}
                                            />

                                            {isFirstRow && (
                                                <>
                                                    {dayBookings.map((booking) => {
                                                        const interval = getBookingInterval(booking);
                                                        return renderBookingBlock(
                                                            booking,
                                                            interval,
                                                            `booking-${booking.id || booking.booking_id || interval.start}`,
                                                            date
                                                        );
                                                    })}

                                                    {dayTemplates
                                                        .filter((item) => item.type === 'blocked')
                                                        .map((item) => {
                                                            const interval = getBlockInterval(item);
                                                            return renderBlockedBlock(
                                                                item,
                                                                interval,
                                                                `blocked-${interval.start}`,
                                                                date,
                                                                cellIsPast
                                                            );
                                                        })}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {showConfirmModal && selection && role === 'learner' && (
                <div style={Styles.modalOverlay}>
                    <div style={Styles.modalContent}>
                        <h3 style={{ color: '#0066cc', marginTop: 0 }}>🛒 Secure Booking Block</h3>
                        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', margin: '20px 0', border: '1px solid #cbd5e0', textAlign: 'left' }}>
                            <p style={{ margin: '5px 0' }}>📅 <b>Date:</b> {selection.dateString}</p>
                            <p style={{ margin: '5px 0' }}>🕒 <b>Start Time:</b> {selection.startTime}</p>
                            <p style={{ margin: '5px 0' }}>🕒 <b>End Time:</b> {selection.endTime}</p>

                            <label style={{ display: 'block', marginTop: '10px', marginBottom: '5px', fontWeight: 'bold' }}>⏳ Duration:</label>
                            <select
                                style={Styles.selectStyle}
                                value={Math.max(1, Math.min(4, Math.round((toMinutes(selection.endTime) - toMinutes(selection.startTime)) / 60)))}
                                onChange={(event) => changeDuration(Number(event.target.value))}
                            >
                                <option value={1}>1 Hour</option>
                                <option value={2}>2 Hours</option>
                                <option value={3}>3 Hours</option>
                                <option value={4}>4 Hours</option>
                            </select>

                            <label style={{ display: 'block', marginTop: '12px', marginBottom: '5px', fontWeight: 'bold' }}>📍 Pickup Location:</label>
                            <select
                                style={Styles.selectStyle}
                                value={selection.pickupLocation || primaryAddress}
                                onChange={(event) => changePickupLocation(event.target.value)}
                            >
                                <option value={primaryAddress}>🏠 Home: {primaryAddress}</option>
                                {secondaryAddress && (
                                    <option value={secondaryAddress}>💼 {secondaryLabel}: {secondaryAddress}</option>
                                )}
                            </select>
                        </div>

                        <button type="button" onClick={processBooking} disabled={isBooking} style={Styles.submitBulkBtn}>
                            {isBooking ? 'Booking...' : 'Confirm Appointment'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowConfirmModal(false);
                                setSelection(null);
                            }}
                            style={{ ...Styles.navBtn, marginTop: '10px', width: '100%' }}
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingCalendar;